<template>
  <f7-view main url="/" browser-history browser-history-separator="">
    <!--
      The PWA install dialog is rendered imperatively, NOT declaratively here.
      `requestInstall()` (called below) queues it in the nudge scheduler, whose
      presenter lazily imports `@khmyznikov/pwa-install` and appends a
      `<pwa-install>` element to `document.body` so the heavy web component
      stays out of the critical bundle until it is actually shown. See
      `usePwaInstall.js` and `useEngagementNudges.js`.
    -->
    <!--
      Not on the collector's screens: they say "offline" themselves (the sync
      pill, the day page's banner), and with no tabbar there the bottom toast
      lay over «خلصت المحل» for as long as the phone had no signal — exactly
      when he files the shop's invoice from the phone. Unmounting closes an
      open toast; leaving /collector/ while offline opens it again.
    -->
    <network-status-bar v-if="!onCollector && !replayOn"></network-status-bar>
    <!--
      Deposit-to-activate / blocked lifecycle banner. Gated to the SELLER shell:
      it is a seller-club concern (its tap targets are the seller-shell /wallet/
      and /profile/, and suppliers do not activate by deposit), so it must never
      overlay the suppliers-club shell. clubType is route-derived + reactive, so
      this hides the banner across all of /suppliers/* and shows it throughout
      the seller shell (including tabbar-hidden pushed pages).
    -->
    <membership-lifecycle-banner
      v-if="isSellerShell && !isImpersonating"
    ></membership-lifecycle-banner>
    <!--
      Operator "acting as a member" strip. Sibling of the lifecycle banner (not
      gated on isSellerShell) so it shows across BOTH shells; renders on
      isImpersonating alone. It and the lifecycle banner are mutually exclusive
      (the lifecycle banner is suppressed above while impersonating) so the two
      never contend for the fixed top slot. The content offset is applied via
      the `acting-as-active` html class toggled below, not by this element.
    -->
    <acting-as-banner></acting-as-banner>
  </f7-view>
</template>

<script setup>
import { f7ready } from 'framework7-vue';
import { computed, onMounted, onScopeDispose, provide, watch } from 'vue';
import { CLUB_MANIFEST, CLUB_TYPES } from '@/clubs/manifest.js';
import { isReplayOn } from '@/collector/previewReplay.js';
import { isCollectorPath } from '@/collector/session.js';
import ActingAsBanner from '@/components/actAs/ActingAsBanner.vue';
import NetworkStatusBar from '@/components/common/NetworkStatusBar.vue';
import MembershipLifecycleBanner from '@/components/membership/MembershipLifecycleBanner.vue';
import { useAccountGate } from '@/composables/useAccountGate';
import { useAuthSessionRefresh } from '@/composables/useAuthSessionRefresh.js';
import { useClubContext } from '@/composables/useClubContext';
import { usePwaInstall } from '@/composables/usePwaInstall.js';
import { useShellRoute } from '@/composables/useShellRoute';
import { useSupplierPreview } from '@/composables/useSupplierPreview.js';
import { useTabbarVisibility } from '@/composables/useTabbarVisibility';
import { isPwa } from '@/platform/index.js';
import { useAuthStore } from '@/stores/authStore.js';

const { requestInstall } = usePwaInstall();

// Visibility/focus refetch — registered once here so every authenticated
// route benefits without per-page wiring (plan Section 1.5).
useAuthSessionRefresh();

const isPWA = computed(() => isPwa());

const { currentRoute } = useShellRoute();
const { showTabbar, showSupplierTabbar } = useTabbarVisibility(currentRoute);

// Seller-shell gate for the membership lifecycle banner (see template). Reuses
// the same route-derived club context the tabbars use, so the banner never
// overlays the suppliers-club shell.
const { clubType } = useClubContext(currentRoute);
const isSellerShell = computed(() => clubType.value === CLUB_TYPES.SELLERS);

// The collector's surface keeps its own offline signs (see the template). Before
// the router has a route, the address bar answers.
const onCollector = computed(() => isCollectorPath(currentRoute.value?.path ?? undefined));
// An offline supplier preview (supplier-preview-offline L-J): the shop owner holds
// the phone — no "no signal" bar over his showroom.
const replayOn = isReplayOn();

// Act-as impersonation. `isImpersonating` both suppresses the seller-shell
// lifecycle banner (they share the fixed top slot) and drives the ActingAsBanner.
// The `acting-as-active` class on <html> bumps --f7-safe-area-top so per-page
// navbars clear the taller strip (offset rule lives in src/css/app.css).
const authStore = useAuthStore();
const isImpersonating = computed(() => authStore.isImpersonating);
// The banner's second reading — a collector previewing a shop's supplier app
// (supplier-preview P2-A) — takes the same strip, so it takes the same offset.
const bannerActive = computed(() => isImpersonating.value || authStore.isSupplierPreview);

// A preview that ends without the banner (expired, read on a profile refresh)
// leaves him in a supplier shell he cannot browse and no banner to leave by —
// back to his day. Enter and exit reload on their own (useSupplierPreview).
const supplierPreview = useSupplierPreview();
watch(
  () => authStore.isSupplierPreview,
  (active, wasActive) => {
    if (wasActive && !active) supplierPreview.onEndedElsewhere();
  },
);

watch(
  bannerActive,
  (active) => {
    document.documentElement.classList.toggle('acting-as-active', active === true);
  },
  { immediate: true },
);

// AppLayout is the persistent authenticated shell and only unmounts via a hard
// reload today (which resets the DOM), so this is inert now — but if a future
// refactor ever swaps the layout without a reload, ensure the offset class does
// not linger on <html> with no watcher left to clear it.
onScopeDispose(() => {
  document.documentElement.classList.remove('acting-as-active');
});

// Two mutually-exclusive shell tabbars: the seller TabbarNavigation injects
// `showTabbar`, the supplier SupplierTabbarNavigation injects
// `showSupplierTabbar`. Both decisions are club-aware and made in
// useTabbarVisibility so neither tabbar bleeds onto the other shell.
provide('showTabbar', showTabbar);
provide('showSupplierTabbar', showSupplierTabbar);

const { isSuspended, openSuspensionBanner } = useAccountGate();

function maybeShowSuspensionBanner() {
  if (isSuspended.value) {
    f7ready(() => openSuspensionBanner());
  }
}

// AppLayout is mounted from App.vue only after `isReady` flips true (auth
// resolved). The session's boot asks for the install card; the scheduler
// offers it to a seller only once a push nudge was answered in an earlier
// session (after-value-moment) and drops it for every other club.
onMounted(() => {
  if (isPWA.value) requestInstall('session');
  maybeShowSuspensionBanner();
});

// Suppliers' install trigger (after-first-browse): the first supplier-shell tab
// load after login. Once per boot and user; the scheduler drops it for other
// clubs. Watched with the club type and the session, which can settle after the
// first supplier path (a cached session refreshed in the background).
let firstBrowseAskedFor = null;
watch(
  [() => currentRoute.value?.path, clubType, () => authStore.user?.id ?? null],
  ([path, club, userId]) => {
    if (userId === null || userId === firstBrowseAskedFor || !isPWA.value) return;
    const inSupplierShell = path?.startsWith(CLUB_MANIFEST.suppliers.root) === true;
    if (club !== CLUB_TYPES.SUPPLIERS || !inSupplierShell || !authStore.isAuthenticated) return;
    if (requestInstall('first-browse')) firstBrowseAskedFor = userId;
  },
  { immediate: true },
);

// Status can flip from null → SUSPENDED on the first background refresh after
// a cached login; re-trigger when that happens.
watch(isSuspended, (suspended) => {
  if (suspended) maybeShowSuspensionBanner();
});
</script>

<style scoped>
.telegram-mini-app {
  background-color: var(--tg-theme-bg-color, #ffffff);
  color: var(--tg-theme-text-color, #000000);
}

.telegram-mini-app .navbar {
  background-color: var(--tg-theme-bg-color, #ffffff);
  color: var(--tg-theme-text-color, #000000);
}

.telegram-mini-app .toolbar {
  background-color: var(--tg-theme-bg-color, #ffffff);
}

.telegram-mini-app .button {
  background-color: var(--tg-theme-button-color, #4caf50);
  color: var(--tg-theme-button-text-color, #ffffff);
}

.telegram-mini-app a {
  color: var(--tg-theme-link-color, #007aff);
}
</style>
